import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { AccountAccessOptionsFields } from 'src/components/AccountAccessOptions/AccountAccessOptions'
import OrgRoleRadioItem from 'src/components/OrgRoleRadioItem/OrgRoleRadioItem'
import OrgRoleRadioItems from 'src/components/OrgRoleRadioItems/OrgRoleRadioItems'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

interface SelectRoleOnInviteProps {
  setRole?: (role: OrganizationRoles) => void
  setBookkeeperAccess?: (fields: AccountAccessOptionsFields) => void
}

const SelectRoleOnInvite: React.FC<SelectRoleOnInviteProps> = (props) => {
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const currentUser = useCurrentUser()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentOrganization?.id,
  })

  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })

  const [canOwnerUpdate, setCanOwnerUpdate] = useState<boolean | undefined>(
    currentUser?.hasOrgOwnerPermissions,
  )
  const [canAdminUpdate, setCanAdminUpdate] = useState<boolean | undefined>(
    currentUser?.hasOrgAdminPermissions,
  )
  const [selectedRole, setSelectedRole] = useState<OrganizationRoles>(
    OrganizationRoles.member,
  )
  const [hoveredRole, setHoveredRole] = useState<OrganizationRoles | undefined>(
    undefined,
  )

  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const translate = useTranslate()

  // update user permission when user role has changed (for owner permission)
  useEffect(() => {
    setCanOwnerUpdate(currentUser?.hasOrgOwnerPermissions)
  }, [currentUser])

  // update user permission when user role has changed (for admin permission)
  useEffect(() => {
    setCanAdminUpdate(currentUser?.hasOrgAdminPermissions)
  }, [currentUser, currentOrganization])

  const memberItem = (
    <OrgRoleRadioItem
      title={translate('Member')}
      description={translate(
        'Default user role. Members can see and interact with all company members.',
      )}
      key={'Member'}
      setRole={(): void => {
        if (props.setRole) props.setRole(OrganizationRoles.member)
        setSelectedRole(OrganizationRoles.member)
      }}
      role={OrganizationRoles.member}
      selectedRole={selectedRole}
      hoveredRole={hoveredRole}
    />
  )

  const bookkeeperItem = (
    <OrgRoleRadioItem
      title={translate('Bookkeeper')}
      description={translate(
        'Has extra tools to manage spend and reconciliation. Allowed full or select visibility across the company.',
      )}
      key={'Bookkeeper'}
      bookkeeperSelected={selectedRole === OrganizationRoles.bookkeeper} // checking that the bookkeeper is selected before showing bookkeeper access components
      setAccountAccess={props.setBookkeeperAccess}
      setRole={(): void => {
        if (props.setRole) props.setRole(OrganizationRoles.bookkeeper)
        setSelectedRole(OrganizationRoles.bookkeeper)
      }}
      role={OrganizationRoles.bookkeeper}
      selectedRole={selectedRole}
    />
  )

  let cardManagerCopy = ''
  if (orgFeatureFlags?.cardManagerRoleEnabled) {
    if (hasVirtualCardFeatures) {
      cardManagerCopy = translate(
        "Can register accounts for virtual card and budget creation. Allowed select edit access to accounts at Owner's discretion.",
      )
    } else {
      cardManagerCopy = translate(
        'Can register physical cards in order to track spend.',
      )
    }
  } else {
    cardManagerCopy = translate(
      'Has extra tools to manage spend and reconciliation. Bookkeepers have selective visibility to accounts managed within the org',
    )
  }

  const cardManagerItem = (
    <OrgRoleRadioItem
      title={translate('Card Manager')}
      description={cardManagerCopy}
      key={'Card Manager'}
      setRole={(): void => {
        if (props.setRole) props.setRole(OrganizationRoles.cardManager)
        setSelectedRole(OrganizationRoles.cardManager)
      }}
      role={OrganizationRoles.cardManager}
      selectedRole={selectedRole}
    />
  )

  const adminItem = (
    <OrgRoleRadioItem
      title={translate('Admin')}
      description={translate(
        orgFeatureFlags?.ownerAdminEnhancementsEnabled
          ? 'Can invite users, manage roles, and configure policies. Has full visibility and edit access for accounts across the company.'
          : 'Can invite users to the org and manage roles. Admins can configure expense policies and have full visibility across the company.',
      )}
      key={'Admin'}
      setRole={(): void => {
        if (props.setRole) props.setRole(OrganizationRoles.admin)
        setSelectedRole(OrganizationRoles.admin)
      }}
      role={OrganizationRoles.admin}
      selectedRole={selectedRole}
    />
  )

  let guestCopy = ''
  if (orgFeatureFlags?.cardManagerRoleEnabled) {
    if (hasVirtualCardFeatures) {
      guestCopy = translate(
        'Can view virtual cards, attach receipts and submit reimbursement reports. Guests cannot request virtual cards or budgets and they also cannot view org details or members.',
      )
    } else {
      guestCopy = translate(
        'Can submit reimbursement reports and attach receipts. Guests cannot view org details or members.',
      )
    }
  } else {
    if (hasVirtualCardFeatures) {
      guestCopy = translate(
        'Can view virtual cards, attach receipts and submit reimbursement reports. Guests cannot request objects or view organization details and members.',
      )
    } else {
      guestCopy = translate(
        'Can submit reimbursement reports and attach receipts. Guests cannot view org details or members.',
      )
    }
  }

  const guestItem = (
    <OrgRoleRadioItem
      title={translate('Guest')}
      description={guestCopy}
      key={'Guest'}
      setRole={(): void => {
        if (props.setRole) props.setRole(OrganizationRoles.guest)
        setSelectedRole(OrganizationRoles.guest)
      }}
      role={OrganizationRoles.guest}
      selectedRole={selectedRole}
    />
  )

  const roleItems = [memberItem, bookkeeperItem]
  if (orgFeatureFlags?.cardManagerRoleEnabled) {
    roleItems.push(cardManagerItem)
  }
  if (canOwnerUpdate) {
    roleItems.push(adminItem)
  }
  if (orgFeatureFlags?.guestRoleEnabled) {
    roleItems.push(guestItem)
  }

  return (
    <>
      <div
        onBlur={(): undefined => {
          return
        }}
        onFocus={(): undefined => {
          return
        }}
      >
        <div
          ref={dropdownRef}
          onClick={(e): void => {
            e.stopPropagation()
          }}
          onKeyDown={(e): void => {
            e.stopPropagation()
          }}
          role="button"
          tabIndex={0}
          css={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              width: auto;
              right: 20px;
            `)}
          `}
        >
          <>
            <span
              css={css`
                margin-top: 24px;
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 8px;
              `}
            >
              {translate('Assign Roles for Employees')}
            </span>
            {roleItems.length > 0 && (
              <OrgRoleRadioItems
                whiteBackground={!!canAdminUpdate}
                alignStart
                items={roleItems}
                currentRole={selectedRole}
                setRole={(role: OrganizationRoles): void => {
                  setSelectedRole(role)
                }}
                setHover={setHoveredRole}
                currentHover={hoveredRole}
                useRadioItemStyle
                showGuestRole={orgFeatureFlags?.guestRoleEnabled}
              />
            )}
          </>
        </div>
      </div>
    </>
  )
}

export default SelectRoleOnInvite
